import { ActivatedRoute, Params, Router, RouterLink } from '@angular/router';
import { AuthService, GeologicalSampleService, NotificationService } from '@services';
import { Component, computed, effect, inject, signal, untracked } from '@angular/core';
import { environment } from '@environment';
import { GeologicalSampleFilterComponent } from '@components/geology/sample/list/filter/dialog.component';
import { HttpParams, httpResource } from '@angular/common/http';
import { LeafletGroupComponent } from '@components/leaflet/group/group.component';
import { ListComponent } from '@components/list/list.component';
import { MatButtonModule } from '@angular/material/button';
import { MatCardModule } from '@angular/material/card';
import { MatChipsModule } from '@angular/material/chips';
import { MatDialog } from '@angular/material/dialog';
import { MatIconModule } from '@angular/material/icon';
import { MatListModule } from '@angular/material/list';
import { MatPaginatorModule, PageEvent } from '@angular/material/paginator';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';

import {
  GeoJSONToWKT,
  GeologicalSampleListResponse,
  Permission,
  WKTToGeoJSON,
} from '@models';

@Component({
  selector: 'app-geology-sample-list',
  templateUrl: './list.component.html',
  styleUrls: ['./list.component.scss'],
  imports: [
    LeafletGroupComponent,
    ListComponent,
    MatButtonModule,
    MatCardModule,
    MatChipsModule,
    MatIconModule,
    MatListModule,
    MatPaginatorModule,
    MatProgressSpinnerModule,
    RouterLink,
  ],
})
export class GeologicalSampleListComponent {
  // Environment variables
  kinko = environment.kinkoHost;
  geologicalSampleDatasetId = environment.geologicalSampleDatasetId;

  // Base URLs to fetch web preview and thumbnail
  thumbBaseAddr =
    this.kinko +
    '/dataset/' +
    this.geologicalSampleDatasetId +
    '/attachment/_blob' +
    '?prefix=/images/thumb/' +
    '&skip=0&take=1&count=true&order=created:asc';

  // Router
  route = inject(ActivatedRoute);
  router = inject(Router);

  // Services
  authService = inject(AuthService);
  geologicalSampleService = inject(GeologicalSampleService);
  notificationService = inject(NotificationService);

  // Permissions
  permissions = signal<Permission | null | undefined>(
    this.route.snapshot.data['permissions'],
  );

  mapExpanded = signal<boolean>(false);

  // Dialog
  dialog = inject(MatDialog);

  // Search
  search = signal<string>(this.route.snapshot.queryParams['q'] || '');

  // Location filter
  location = signal<string>(this.route.snapshot.queryParams['location']);
  geojson = computed(() => WKTToGeoJSON(this.location()));

  // Content filters
  filters = signal<string|string[]|undefined>(this.route.snapshot.queryParams['filter']);
  parsedFilters = computed<string[]>(() => {
    const f = this.filters();
    if (Array.isArray(f)) {
      return f as string[];
    } else return f ? [f as string] : [];
  });

  // Pagination
  defaultPageSize = 10;
  pageSizeOptions = [this.defaultPageSize, 20, 50, 100];
  page = signal(parseInt(this.route.snapshot.queryParams['page']) || 0);
  pageSize = signal(parseInt(this.route.snapshot.queryParams['pageSize']) || this.defaultPageSize);

  // ListResource
  count = signal(0);
  sampleList = httpResource<GeologicalSampleListResponse>(() => {
    const count = untracked(() => this.count() === 0 || this.page() === 0);
    let httpParams = new HttpParams()
      .set('skip', this.page() * this.pageSize())
      .set('take', this.pageSize())
      .set('count', count)
      .set('order', 'created:asc')
      .set('q', this.search() || '')

    const location = this.location();
    if (location) {
      httpParams = httpParams.set('location', location);
    }

    const filters = this.parsedFilters();
    if (!this.authService.user()) {
      if (!this.parsedFilters().find( o => o === 'draft:false')) {
        filters.push('draft:false');
      }
    }

    for (const f of filters) {
      httpParams = httpParams.append('filter',f);
    }

    return `${this.kinko}/dataset/${this.geologicalSampleDatasetId}/record/?${httpParams.toString()}`
  })

  constructor() {
    // Effect used to update the count signal. This prevents count from being
    // fetched on every request.
    effect(() => {
      if (!this.sampleList.hasValue() || !this.sampleList.value().count) {
        return;
      }

      this.count.set(this.sampleList.value().count);
    })

    // Update results on query updates
    this.route.queryParams.subscribe((params: Params) => {
      this.page.set(params['page'] || 0);
      this.pageSize.set(params['pageSize'] || this.defaultPageSize);
      this.search.set(params['q'] || '');
      this.location.set(params['location'] || '');
      this.filters.set(params['filter']);

      if (this.location()) {
        this.mapExpanded.set(true);
      } else {
        this.mapExpanded.set(false);
      }
    });
  }

  onSearch(q: string, location: string, page?: number, pageSize?: number) {
    const take = pageSize === undefined ? this.pageSize() : pageSize;

    this.router.navigate([], {
      relativeTo: this.route,
      queryParamsHandling: 'merge',
      queryParams: {
        q: q || undefined,
        location: location || undefined,
        page: page || undefined,
        pagSize: take === this.defaultPageSize ? undefined : take,
      },
      replaceUrl: true
    });
  }

  advancedSearch() {
    let geologist = undefined;
    let expedition = undefined;

    // See if a geologist or expedition filter is set.
    for (const f of this.parsedFilters()) {
      if (f.split(':')[0] && f.split(':')[0] === 'geologists') {
        geologist = f.split(':')[1];
      }
      if (f.split(':')[0] && f.split(':')[0] === 'expedition') {
        expedition = f.split(':')[1];
      }
    }

    // Open the advanced filter dialog
    this.dialog.open(GeologicalSampleFilterComponent, {
      data: {
        geologist: geologist,
        expedition: expedition,
        geojson: this.geojson(),
      },
      minWidth: '360px',
      maxWidth: '960px',
    }).afterClosed().subscribe(
      (data: {
        geologist: string,
        expedition: string,
        geojson: GeoJSON.FeatureCollection | undefined,
      } | undefined) => {
        if (!data) {
          return;
        }

        let location: string | undefined = undefined;
        if (data.geojson?.features.length) {
          location = GeoJSONToWKT(data.geojson);
        }

        let params = {
          q: this.search(),
          location: location,
          page: undefined,
        }

        const filters = [];
        if (data.geologist) {
          filters.push('geologists:'+data.geologist)
        }

        if (data.expedition) {
          filters.push('expedition:'+data.expedition)
        }

        params = { ...params, ...{filter: filters}};
        this.router.navigate([], {
          relativeTo: this.route,
          queryParamsHandling: 'merge',
          queryParams: params,
          replaceUrl: true,
        });
      }
    )
  }

  onFilterClear(filter: string) {
    if (!filter) {
      return;
    }

    if (Array.isArray(this.filters())) {
      const filters = this.filters() || [];
      const keep = [];
      for (const f of filters) {
        if (f !== filter) {
          keep.push(f)
        }
      }
      this.filters.set(keep);
    } else if (filter === this.filters()) {
      this.filters.set(undefined);
    }

    this.router.navigate([], {
      relativeTo: this.route,
      queryParamsHandling: 'merge',
      queryParams: {
        filter: this.filters(),
      },
      replaceUrl: true,
    });
  }

  onPage(event: PageEvent) {
    this.router.navigate([], {
      relativeTo: this.route,
      queryParamsHandling: 'merge',
      queryParams: {
        q: this.search() || undefined,
        page: event.pageIndex || undefined,
        pageSize:
          event.pageSize === this.defaultPageSize ? undefined : event.pageSize,
      },
      replaceUrl: true,
    });
  }
}
